import axios, { type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import Locals from '../providers/Locals';
import { captureApiError } from '@odo/shared-ui/observability/captureApiError';
import { redirectToLogin } from './redirectToLogin';

declare module 'axios' {
    interface AxiosRequestConfig {
        // Set on actions whose caller toasts a 403, so the page isn't swapped for /forbidden.
        keepPageOnForbidden?: boolean;
    }
}

const API_BASE_URL = Locals.config().base_api_url;

const api = axios.create({
    baseURL: API_BASE_URL + '/api/v1',
    headers: {
        'Content-Type': 'application/json'
    }
});

const files_api = axios.create({
    baseURL: API_BASE_URL + '/api/v1'
});

const requestInterceptor = (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('odo_login_token');
    if (token) {
        config.headers.set('Authorization', `Bearer ${token}`);
    }
    return config;
};

const requestErrorInterceptor = (error: unknown) => {
    const message = error instanceof Error ? error.message : 'Request failed';
    return Promise.reject(new Error(message));
};

const responseInterceptor = (response: AxiosResponse) => response;

const responseErrorInterceptor = (error: unknown) => {
    const axiosError = error as {
        response?: { status?: number };
        config?: { keepPageOnForbidden?: boolean };
    };
    const status = axiosError.response?.status;

    if (status === 401 && globalThis.location.pathname !== '/login') {
        redirectToLogin();
    } else if (
        status === 403 &&
        !axiosError.config?.keepPageOnForbidden &&
        globalThis.location.pathname !== '/forbidden'
    ) {
        captureApiError(error);
        globalThis.location.href = '/forbidden';
    } else {
        captureApiError(error);
    }

    return Promise.reject(error);
};

api.interceptors.request.use(requestInterceptor, requestErrorInterceptor);
api.interceptors.response.use(responseInterceptor, responseErrorInterceptor);

files_api.interceptors.request.use(requestInterceptor, requestErrorInterceptor);
files_api.interceptors.response.use(responseInterceptor, responseErrorInterceptor);

export default api;
export { files_api };
